import { useState } from 'react';
import { Modal } from '../../../components/Modal';
import Button from '../../../components/Button';
import ImagePicker from '../../../pickers/images/ImagePicker';
import { SpeechBubblePreview } from './SpeechBubblePreview';
import type { JourneyCardVariant, JourneyChapterTemplateFirestore } from '../services/journeyTypes';

interface EditChapterModalProps {
    chapter: JourneyChapterTemplateFirestore;
    onSave: (chapter: JourneyChapterTemplateFirestore) => void;
    onCancel: () => void;
}

const COLOR_OPTIONS: JourneyCardVariant[] = ['yellow', 'blue', 'green', 'pink'];

export const EditChapterModal = ({ chapter, onSave, onCancel }: EditChapterModalProps) => {
    const [formData, setFormData] = useState<JourneyChapterTemplateFirestore>({ ...chapter });
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (field: keyof JourneyChapterTemplateFirestore, value: string | number) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        setIsSaving(true);
        try {
            await onSave(formData);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen onClose={onCancel} title={`Edit Chapter ${chapter.number}`} size="lg">
            <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="chapter-title" className="block text-sm font-medium text-gray-700 mb-1">
                            Title *
                        </label>
                        <input
                            type="text"
                            id="chapter-title"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Chapter title"
                        />
                    </div>
                    <div>
                        <label htmlFor="chapter-number" className="block text-sm font-medium text-gray-700 mb-1">
                            Number *
                        </label>
                        <input
                            type="number"
                            id="chapter-number"
                            value={formData.number}
                            onChange={(e) => handleChange('number', parseInt(e.target.value, 10) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            min={1}
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="chapter-description" className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                    </label>
                    <textarea
                        id="chapter-description"
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Chapter description"
                        rows={3}
                    />
                </div>

                <div>
                    <label htmlFor="chapter-ollieText" className="block text-sm font-medium text-gray-700 mb-1">
                        Ollie Text
                    </label>
                    <textarea
                        id="chapter-ollieText"
                        value={formData.ollieText}
                        onChange={(e) => handleChange('ollieText', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Ollie text"
                        rows={3}
                    />
                    {formData.ollieText && (
                        <div className="mt-2">
                            <span className="block text-xs text-gray-500 mb-1">Phone preview</span>
                            <div className="w-[375px] max-w-full mx-auto rounded-xl border border-gray-200 bg-osGrey-5 p-4 flex justify-center">
                                <SpeechBubblePreview text={formData.ollieText} />
                            </div>
                        </div>
                    )}
                </div>

                <ImagePicker
                    value={formData.imageUrl}
                    onChange={(url) => handleChange('imageUrl', url)}
                    folder="journey-chapters"
                    label="Chapter Image"
                    placeholder="No chapter image selected"
                    aspectRatio="aspect-square"
                    height="h-40"
                />

                <div>
                    <label htmlFor="chapter-color" className="block text-sm font-medium text-gray-700 mb-1">
                        Color
                    </label>
                    <select
                        id="chapter-color"
                        value={formData.color}
                        onChange={(e) => handleChange('color', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        {COLOR_OPTIONS.map((color) => (
                            <option key={color} value={color}>
                                {color.charAt(0).toUpperCase() + color.slice(1)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <Button onClick={onCancel} variant="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant="primary" loading={isSaving} disabled={isSaving}>
                        Save
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
